/** Shared utilities for Performance sub-views. */
import { ANDON_SUFFIXES } from './TimingTable.js';
import { eventKey, gateTxnKey } from './eventKeys.js';
import { andonLifecyclesFrom } from './andonLifecycle.js';

// ─── Top Impact helpers ───

export const fmtDur = secs => {
    if (secs == null) return '';
    const m = Math.floor(secs / 60), s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
};

export const fmtTime = ts =>
    new Date(ts * 1000).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' });

// ─── Driver Breakdown helpers ───

function _avgSecs(arr) {
    return arr.length ? Math.round(arr.reduce((s, v) => s + v, 0) / arr.length) : null;
}

function _ensureDriver(drivers, uid) {
    if (!drivers.has(uid)) {
        drivers.set(uid, {
            hostlerCompletes: [], shuttleCompletes: [], sub4: 0,
            andonCompletes: [], andonDurations: [], checkIns: 0, checkOuts: 0,
            rawCheckIns: 0, rawCheckOuts: 0, hasNonR4GCheck: false,
        });
    }
    return drivers.get(uid);
}

/**
 * Return one attributable row for each effective physical gate transaction.
 * A combined tractor-and-trailer gate scan produces separate equipment rows
 * with the same event type and timestamp. Keep only one so this breakdown
 * aligns with End of Shift TPH. Prefer an event with a user ID so the
 * transaction remains attributable when only one equipment row has an operator.
 *
 * @param {Object[]} events
 * @returns {Array<{event: Object, rawCount: number}>}
 */
function _effectiveGateTransactions(events) {
    const transactions = new Map();
    for (const event of events) {
        const key = gateTxnKey(event);
        const recorded = transactions.get(key);
        if (!recorded) {
            transactions.set(key, { event, rawCount: 1 });
            continue;
        }

        recorded.rawCount++;
        if (!recorded.event.userId && event.userId) {
            recorded.event = event;
        }
    }
    return [...transactions.values()];
}

/**
 * Build per-driver stat rows from a complete performanceDataService cache object.
 * @param {Object} data - performanceDataService._cache
 * @param {boolean} [showAll=false] - if true, skip noise filtering
 * @returns {Array<Object>}
 */
export function buildDriverRows(data, showAll = false) {
    const { onsitePairs = [], offsitePairs = [], checkInEvents = [], checkOutEvents = [] } = data;
    const allPairs = [...onsitePairs, ...offsitePairs];
    // Composite eventKey (not eventId): two completes can collide on eventId, which would
    // otherwise misclassify an onsite complete as offsite (shuttle) or vice versa.
    const offsiteIds = new Set(offsitePairs.map(p => eventKey(p.complete)));

    const drivers = new Map();
    for (const p of allPairs) {
        const uid = p.complete.userId || p.start?.userId;
        if (!uid) continue;
        const d = _ensureDriver(drivers, uid);
        const dur = p.complete.timestamp - p.start.timestamp;
        if (offsiteIds.has(eventKey(p.complete))) {
            d.shuttleCompletes.push(dur);
        } else {
            d.hostlerCompletes.push(dur);
            if (dur < 240) d.sub4++;
        }
    }

    // START → COMPLETE (not CREATE → COMPLETE) of the same andon lifecycle, because START
    // is when the driver actually began working the andon. Lifecycles come from the shared
    // order-independent resolver (published by PerformanceDataService when available).
    for (const lc of andonLifecyclesFrom(data, ANDON_SUFFIXES)) {
        if (lc.status !== 'completed' || !lc.start) continue;
        const uid = lc.complete.userId || lc.start.userId;
        if (!uid) continue;
        const d = _ensureDriver(drivers, uid);
        d.andonCompletes.push(lc.complete);
        d.andonDurations.push(lc.responseSecs);
    }

    // Collapse tractor-and-trailer equipment rows from the same gate scan so
    // these counts use the same effective-transaction logic as End of Shift.
    for (const { event: e, rawCount } of _effectiveGateTransactions(checkInEvents)) {
        if (!e.userId) continue;
        const d = _ensureDriver(drivers, e.userId);
        d.checkIns++;
        d.rawCheckIns += rawCount;
        if (e.systemName !== 'R4G') d.hasNonR4GCheck = true;
    }
    for (const { event: e, rawCount } of _effectiveGateTransactions(checkOutEvents)) {
        if (!e.userId) continue;
        const d = _ensureDriver(drivers, e.userId);
        d.checkOuts++;
        d.rawCheckOuts += rawCount;
        if (e.systemName !== 'R4G') d.hasNonR4GCheck = true;
    }

    return [...drivers.entries()]
        .map(([uid, d]) => ({
            uid,
            transactions:     d.hostlerCompletes.length + d.shuttleCompletes.length + d.andonCompletes.length + d.checkIns + d.checkOuts,
            hostlerCompletes: d.hostlerCompletes.length,
            avgHostler:       _avgSecs(d.hostlerCompletes),
            shuttleCompletes: d.shuttleCompletes.length,
            avgShuttle:       _avgSecs(d.shuttleCompletes),
            sub4:             d.sub4,
            andonCompletes:   d.andonCompletes.length,
            avgAndon:         _avgSecs(d.andonDurations),
            checkIns:         d.checkIns,
            checkOuts:        d.checkOuts,
            rawCheckIns:      d.rawCheckIns,
            rawCheckOuts:     d.rawCheckOuts,
            hasNonR4GCheck:   d.hasNonR4GCheck,
        }))
        .filter(r => showAll || (r.uid !== '0-click' && (r.hostlerCompletes || r.shuttleCompletes || r.andonCompletes || r.checkIns + r.checkOuts >= 10 || r.hasNonR4GCheck)))
        .sort((a, b) => b.transactions - a.transactions);
}

// ─── Top Impact helpers ───

/**
 * Sort pairs by duration descending and map to plain row objects.
 * @param {Array<{create, start, complete}>} pairs
 * @param {'c2c'|'c2s'|'s2c'} type
 * @returns {Array<Object>}
 */
export function pairsToImpactRows(pairs, type) {
    const eligible = type === 's2c' ? pairs : pairs.filter(p => p.create);
    return [...eligible]
        .sort((a, b) => {
            const dur = p => type === 'c2s' ? p.start.timestamp - p.create.timestamp
                           : type === 's2c' ? p.complete.timestamp - p.start.timestamp
                           :                  p.complete.timestamp - p.create.timestamp;
            return dur(b) - dur(a);
        })
        .slice(0, 15)
        .map(p => ({
            driver:   (p.complete?.userId || p.start?.userId || '').replace('@amazon.com', ''),
            trailer:  p.create?.vehicleNumber || p.start?.vehicleNumber || '',
            path:     `${p.start?.location || '?'}→${p.complete?.location || '?'}`,
            create:   p.create?.timestamp ?? null,
            start:    p.start?.timestamp  ?? null,
            complete: p.complete?.timestamp ?? null,
            duration: type === 'c2s' ? p.start.timestamp    - p.create.timestamp
                    : type === 's2c' ? p.complete.timestamp - p.start.timestamp
                    :                  p.complete.timestamp - p.create.timestamp,
        }));
}

export const toInputValue = ms => {
    const d = new Date(ms);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const fmt = ts => new Date(ts * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit',
});

// shiftDefaults (TCS preset/storage-driven) is replaced by JarJar's own shift picker.
